/**
 * Fantasy Console 0 - Theme System
 *
 * JS-first theme definitions. On theme change, applyTheme() writes all values
 * as CSS custom properties on document.documentElement. Canvas components read
 * colors directly from the theme object via React context.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ThemeColors {
  // Backgrounds
  bgPrimary: string
  bgSecondary: string
  bgTertiary: string
  bgElevated: string
  bgHover: string

  // Borders
  borderSubtle: string
  borderDefault: string
  borderFocus: string

  // Text
  textPrimary: string
  textSecondary: string
  textMuted: string

  // Accents
  accentPrimary: string
  accentPrimaryHover: string
  accentSecondary: string
  accentWarning: string
  accentError: string
  accentErrorHover: string
  accentHighlight: string

  // Canvas (PianoRoll)
  canvasBg: string
  canvasGridBgAlt: string
  canvasGridFine: string
  canvasHoverText: string

  // Contrast overlays (white-on-dark → dark-on-light)
  overlayStrong: string
  overlayMediumStrong: string
  overlayMedium: string
  overlaySubtle: string
  overlayFaint: string
  overlayGhost: string
  overlayWhisper: string
  overlayTrace: string

  // Accent-derived transparency
  accentPrimaryAlpha08: string
  accentPrimaryAlpha30: string
  accentPrimaryAlpha60: string

  // Error-derived transparency
  errorBgSubtle: string
  errorBg: string

  // Highlight-derived transparency
  highlightBgSubtle: string

  // CodeMirror
  cmSelectionBg: string

  // Shadows
  shadowSm: string
  shadowMd: string
  shadowLg: string
  shadowSubtle: string
  shadowCard: string
}

export interface Theme {
  id: string
  name: string
  isDark: boolean
  colors: ThemeColors
}

// ---------------------------------------------------------------------------
// Built-in themes
// ---------------------------------------------------------------------------

export const DARK_THEME: Theme = {
  id: 'dark',
  name: 'Dark',
  isDark: true,
  colors: {
    bgPrimary: '#060606',
    bgSecondary: '#1d1e20',
    bgTertiary: '#2a2c30',
    bgElevated: '#343639',
    bgHover: '#2a2c30',

    borderSubtle: '#333333',
    borderDefault: '#505050',
    borderFocus: '#727272',

    textPrimary: '#e0e0e0',
    textSecondary: '#a0a0a0',
    textMuted: '#707070',

    accentPrimary: '#29adff',
    accentPrimaryHover: '#5cbfff',
    accentSecondary: '#00e436',
    accentWarning: '#ffec27',
    accentError: '#ff004d',
    accentErrorHover: '#ff3366',
    accentHighlight: '#ff77a8',

    canvasBg: '#0f0f0f',
    canvasGridBgAlt: '#080808',
    canvasGridFine: '#1a1a1a',
    canvasHoverText: '#aaaaaa',

    overlayStrong: 'rgba(255, 255, 255, 0.8)',
    overlayMediumStrong: 'rgba(255, 255, 255, 0.7)',
    overlayMedium: 'rgba(255, 255, 255, 0.5)',
    overlaySubtle: 'rgba(255, 255, 255, 0.2)',
    overlayFaint: 'rgba(255, 255, 255, 0.1)',
    overlayGhost: 'rgba(255, 255, 255, 0.06)',
    overlayWhisper: 'rgba(255, 255, 255, 0.04)',
    overlayTrace: 'rgba(255, 255, 255, 0.03)',

    accentPrimaryAlpha08: 'rgba(41, 173, 255, 0.08)',
    accentPrimaryAlpha30: 'rgba(41, 173, 255, 0.3)',
    accentPrimaryAlpha60: 'rgba(41, 173, 255, 0.6)',

    errorBgSubtle: 'rgba(255, 0, 77, 0.1)',
    errorBg: 'rgba(255, 0, 77, 0.15)',

    highlightBgSubtle: 'rgba(255, 119, 168, 0.1)',

    cmSelectionBg: 'rgba(155, 205, 255, 0.5)',

    shadowSm: '0 1px 2px rgba(0, 0, 0, 0.3)',
    shadowMd: '0 4px 6px rgba(0, 0, 0, 0.4)',
    shadowLg: '0 10px 15px rgba(0, 0, 0, 0.5)',
    shadowSubtle: '0 1px 3px rgba(0, 0, 0, 0.3)',
    shadowCard: '0 2px 8px rgba(0, 0, 0, 0.4)',
  },
}

export const LIGHT_THEME: Theme = {
  id: 'light',
  name: 'Light',
  isDark: false,
  colors: {
    bgPrimary: '#f0f0f0',
    bgSecondary: '#ffffff',
    bgTertiary: '#d5d5d5',
    bgElevated: '#e5e5e5',
    bgHover: '#d5d5d5',

    borderSubtle: '#cccccc',
    borderDefault: '#b0b0b0',
    borderFocus: '#888888',

    textPrimary: '#1a1a1a',
    textSecondary: '#555555',
    textMuted: '#888888',

    accentPrimary: '#1a7fd4',
    accentPrimaryHover: '#1565a8',
    accentSecondary: '#009930',
    accentWarning: '#b38600',
    accentError: '#cc0033',
    accentErrorHover: '#990025',
    accentHighlight: '#d4547a',

    canvasBg: '#f0f0f0',
    canvasGridBgAlt: '#e5e5e5',
    canvasGridFine: '#d0d0d0',
    canvasHoverText: '#555555',

    overlayStrong: 'rgba(0, 0, 0, 0.7)',
    overlayMediumStrong: 'rgba(0, 0, 0, 0.55)',
    overlayMedium: 'rgba(0, 0, 0, 0.4)',
    overlaySubtle: 'rgba(0, 0, 0, 0.15)',
    overlayFaint: 'rgba(0, 0, 0, 0.08)',
    overlayGhost: 'rgba(0, 0, 0, 0.06)',
    overlayWhisper: 'rgba(0, 0, 0, 0.04)',
    overlayTrace: 'rgba(0, 0, 0, 0.03)',

    accentPrimaryAlpha08: 'rgba(26, 127, 212, 0.1)',
    accentPrimaryAlpha30: 'rgba(26, 127, 212, 0.3)',
    accentPrimaryAlpha60: 'rgba(26, 127, 212, 0.6)',

    errorBgSubtle: 'rgba(204, 0, 51, 0.08)',
    errorBg: 'rgba(204, 0, 51, 0.12)',

    highlightBgSubtle: 'rgba(212, 84, 122, 0.1)',

    cmSelectionBg: 'rgba(26, 127, 212, 0.25)',

    shadowSm: '0 1px 2px rgba(0, 0, 0, 0.08)',
    shadowMd: '0 4px 6px rgba(0, 0, 0, 0.1)',
    shadowLg: '0 10px 15px rgba(0, 0, 0, 0.12)',
    shadowSubtle: '0 1px 3px rgba(0, 0, 0, 0.06)',
    shadowCard: '0 2px 8px rgba(0, 0, 0, 0.08)',
  },
}

export const MONOKAI_THEME: Theme = {
  id: 'monokai',
  name: 'Monokai',
  isDark: true,
  colors: {
    bgPrimary: '#20211b',
    bgSecondary: '#2e2f2a',
    bgTertiary: '#3e3d32',
    bgElevated: '#49483e',
    bgHover: '#3e3d32',

    borderSubtle: '#3e3d32',
    borderDefault: '#4e4d43',
    borderFocus: '#75715e',

    textPrimary: '#f8f8f2',
    textSecondary: '#c0bfb6',
    textMuted: '#75715e',

    accentPrimary: '#66d9ef',
    accentPrimaryHover: '#89e3f4',
    accentSecondary: '#a6e22e',
    accentWarning: '#e6db74',
    accentError: '#f92672',
    accentErrorHover: '#fb5599',
    accentHighlight: '#fd971f',

    canvasBg: '#1e1f1c',
    canvasGridBgAlt: '#1a1b18',
    canvasGridFine: '#333428',
    canvasHoverText: '#a6a699',

    overlayStrong: 'rgba(248, 248, 242, 0.8)',
    overlayMediumStrong: 'rgba(248, 248, 242, 0.7)',
    overlayMedium: 'rgba(248, 248, 242, 0.5)',
    overlaySubtle: 'rgba(248, 248, 242, 0.2)',
    overlayFaint: 'rgba(248, 248, 242, 0.1)',
    overlayGhost: 'rgba(248, 248, 242, 0.06)',
    overlayWhisper: 'rgba(248, 248, 242, 0.04)',
    overlayTrace: 'rgba(248, 248, 242, 0.03)',

    accentPrimaryAlpha08: 'rgba(102, 217, 239, 0.08)',
    accentPrimaryAlpha30: 'rgba(102, 217, 239, 0.3)',
    accentPrimaryAlpha60: 'rgba(102, 217, 239, 0.6)',

    errorBgSubtle: 'rgba(249, 38, 114, 0.1)',
    errorBg: 'rgba(249, 38, 114, 0.15)',

    highlightBgSubtle: 'rgba(253, 151, 31, 0.1)',

    cmSelectionBg: 'rgba(102, 217, 239, 0.25)',

    shadowSm: '0 1px 2px rgba(0, 0, 0, 0.3)',
    shadowMd: '0 4px 6px rgba(0, 0, 0, 0.4)',
    shadowLg: '0 10px 15px rgba(0, 0, 0, 0.5)',
    shadowSubtle: '0 1px 3px rgba(0, 0, 0, 0.3)',
    shadowCard: '0 2px 8px rgba(0, 0, 0, 0.4)',
  },
}

export const SOLARIZED_DARK_THEME: Theme = {
  id: 'solarized-dark',
  name: 'Solarized Dark',
  isDark: true,
  colors: {
    bgPrimary: '#002b36',
    bgSecondary: '#073642',
    bgTertiary: '#0a4050',
    bgElevated: '#0d4d5e',
    bgHover: '#0a4050',

    borderSubtle: '#0a4050',
    borderDefault: '#124d5e',
    borderFocus: '#2aa198',

    textPrimary: '#eee8d5',
    textSecondary: '#93a1a1',
    textMuted: '#657b83',

    accentPrimary: '#268bd2',
    accentPrimaryHover: '#4aa3e0',
    accentSecondary: '#859900',
    accentWarning: '#b58900',
    accentError: '#dc322f',
    accentErrorHover: '#e55',
    accentHighlight: '#d33682',

    canvasBg: '#001e26',
    canvasGridBgAlt: '#00171e',
    canvasGridFine: '#0a4050',
    canvasHoverText: '#93a1a1',

    overlayStrong: 'rgba(238, 232, 213, 0.8)',
    overlayMediumStrong: 'rgba(238, 232, 213, 0.7)',
    overlayMedium: 'rgba(238, 232, 213, 0.5)',
    overlaySubtle: 'rgba(238, 232, 213, 0.2)',
    overlayFaint: 'rgba(238, 232, 213, 0.1)',
    overlayGhost: 'rgba(238, 232, 213, 0.06)',
    overlayWhisper: 'rgba(238, 232, 213, 0.04)',
    overlayTrace: 'rgba(238, 232, 213, 0.03)',

    accentPrimaryAlpha08: 'rgba(38, 139, 210, 0.08)',
    accentPrimaryAlpha30: 'rgba(38, 139, 210, 0.3)',
    accentPrimaryAlpha60: 'rgba(38, 139, 210, 0.6)',

    errorBgSubtle: 'rgba(220, 50, 47, 0.1)',
    errorBg: 'rgba(220, 50, 47, 0.15)',

    highlightBgSubtle: 'rgba(211, 54, 130, 0.1)',

    cmSelectionBg: 'rgba(38, 139, 210, 0.3)',

    shadowSm: '0 1px 2px rgba(0, 0, 0, 0.4)',
    shadowMd: '0 4px 6px rgba(0, 0, 0, 0.5)',
    shadowLg: '0 10px 15px rgba(0, 0, 0, 0.6)',
    shadowSubtle: '0 1px 3px rgba(0, 0, 0, 0.4)',
    shadowCard: '0 2px 8px rgba(0, 0, 0, 0.5)',
  },
}

export const NORD_THEME: Theme = {
  id: 'nord',
  name: 'Nord',
  isDark: true,
  colors: {
    bgPrimary: '#222834',
    bgSecondary: '#3b4252',
    bgTertiary: '#434c5e',
    bgElevated: '#4c566a',
    bgHover: '#434c5e',

    borderSubtle: '#434c5e',
    borderDefault: '#667084',
    borderFocus: '#81a1c1',

    textPrimary: '#eceff4',
    textSecondary: '#d8dee9',
    textMuted: '#7b88a1',

    accentPrimary: '#88c0d0',
    accentPrimaryHover: '#a3d1de',
    accentSecondary: '#a3be8c',
    accentWarning: '#ebcb8b',
    accentError: '#bf616a',
    accentErrorHover: '#d08770',
    accentHighlight: '#b48ead',

    canvasBg: '#242933',
    canvasGridBgAlt: '#1f242e',
    canvasGridFine: '#434c5e',
    canvasHoverText: '#d8dee9',

    overlayStrong: 'rgba(236, 239, 244, 0.8)',
    overlayMediumStrong: 'rgba(236, 239, 244, 0.7)',
    overlayMedium: 'rgba(236, 239, 244, 0.5)',
    overlaySubtle: 'rgba(236, 239, 244, 0.2)',
    overlayFaint: 'rgba(236, 239, 244, 0.1)',
    overlayGhost: 'rgba(236, 239, 244, 0.06)',
    overlayWhisper: 'rgba(236, 239, 244, 0.04)',
    overlayTrace: 'rgba(236, 239, 244, 0.03)',

    accentPrimaryAlpha08: 'rgba(136, 192, 208, 0.08)',
    accentPrimaryAlpha30: 'rgba(136, 192, 208, 0.3)',
    accentPrimaryAlpha60: 'rgba(136, 192, 208, 0.6)',

    errorBgSubtle: 'rgba(191, 97, 106, 0.1)',
    errorBg: 'rgba(191, 97, 106, 0.15)',

    highlightBgSubtle: 'rgba(180, 142, 173, 0.1)',

    cmSelectionBg: 'rgba(136, 192, 208, 0.25)',

    shadowSm: '0 1px 2px rgba(0, 0, 0, 0.3)',
    shadowMd: '0 4px 6px rgba(0, 0, 0, 0.4)',
    shadowLg: '0 10px 15px rgba(0, 0, 0, 0.5)',
    shadowSubtle: '0 1px 3px rgba(0, 0, 0, 0.3)',
    shadowCard: '0 2px 8px rgba(0, 0, 0, 0.4)',
  },
}

export const WARM_THEME: Theme = {
  id: 'warm',
  name: 'Warm',
  isDark: false,
  colors: {
    bgPrimary: '#f5f0e8',
    bgSecondary: '#faf6ef',
    bgTertiary: '#ddd6c8',
    bgElevated: '#ebe5da',
    bgHover: '#ddd6c8',

    borderSubtle: '#d4cbbe',
    borderDefault: '#bfb5a5',
    borderFocus: '#9a8d7a',

    textPrimary: '#3b3228',
    textSecondary: '#6b5e50',
    textMuted: '#9a8d7a',

    accentPrimary: '#b06830',
    accentPrimaryHover: '#944f1e',
    accentSecondary: '#5a7a3a',
    accentWarning: '#b08a20',
    accentError: '#c44040',
    accentErrorHover: '#a03030',
    accentHighlight: '#b05070',

    canvasBg: '#f5f0e8',
    canvasGridBgAlt: '#ebe5da',
    canvasGridFine: '#d4cbbe',
    canvasHoverText: '#6b5e50',

    overlayStrong: 'rgba(59, 50, 40, 0.7)',
    overlayMediumStrong: 'rgba(59, 50, 40, 0.55)',
    overlayMedium: 'rgba(59, 50, 40, 0.4)',
    overlaySubtle: 'rgba(59, 50, 40, 0.15)',
    overlayFaint: 'rgba(59, 50, 40, 0.08)',
    overlayGhost: 'rgba(59, 50, 40, 0.06)',
    overlayWhisper: 'rgba(59, 50, 40, 0.04)',
    overlayTrace: 'rgba(59, 50, 40, 0.03)',

    accentPrimaryAlpha08: 'rgba(176, 104, 48, 0.1)',
    accentPrimaryAlpha30: 'rgba(176, 104, 48, 0.3)',
    accentPrimaryAlpha60: 'rgba(176, 104, 48, 0.6)',

    errorBgSubtle: 'rgba(196, 64, 64, 0.08)',
    errorBg: 'rgba(196, 64, 64, 0.12)',

    highlightBgSubtle: 'rgba(176, 80, 112, 0.1)',

    cmSelectionBg: 'rgba(176, 104, 48, 0.2)',

    shadowSm: '0 1px 2px rgba(59, 50, 40, 0.08)',
    shadowMd: '0 4px 6px rgba(59, 50, 40, 0.1)',
    shadowLg: '0 10px 15px rgba(59, 50, 40, 0.12)',
    shadowSubtle: '0 1px 3px rgba(59, 50, 40, 0.06)',
    shadowCard: '0 2px 8px rgba(59, 50, 40, 0.08)',
  },
}

export const DRACULA_THEME: Theme = {
  id: 'dracula',
  name: 'Dracula',
  isDark: true,
  colors: {
    bgPrimary: '#181a26',
    bgSecondary: '#2d2f3d',
    bgTertiary: '#383a4a',
    bgElevated: '#44475a',
    bgHover: '#383a4a',

    borderSubtle: '#44475a',
    borderDefault: '#656780',
    borderFocus: '#bd93f9',

    textPrimary: '#f8f8f2',
    textSecondary: '#c0bfca',
    textMuted: '#6272a4',

    accentPrimary: '#bd93f9',
    accentPrimaryHover: '#caa4fa',
    accentSecondary: '#50fa7b',
    accentWarning: '#f1fa8c',
    accentError: '#ff5555',
    accentErrorHover: '#ff6e6e',
    accentHighlight: '#ff79c6',

    canvasBg: '#21222c',
    canvasGridBgAlt: '#1c1d27',
    canvasGridFine: '#383a4a',
    canvasHoverText: '#c0bfca',

    overlayStrong: 'rgba(248, 248, 242, 0.8)',
    overlayMediumStrong: 'rgba(248, 248, 242, 0.7)',
    overlayMedium: 'rgba(248, 248, 242, 0.5)',
    overlaySubtle: 'rgba(248, 248, 242, 0.2)',
    overlayFaint: 'rgba(248, 248, 242, 0.1)',
    overlayGhost: 'rgba(248, 248, 242, 0.06)',
    overlayWhisper: 'rgba(248, 248, 242, 0.04)',
    overlayTrace: 'rgba(248, 248, 242, 0.03)',

    accentPrimaryAlpha08: 'rgba(189, 147, 249, 0.08)',
    accentPrimaryAlpha30: 'rgba(189, 147, 249, 0.3)',
    accentPrimaryAlpha60: 'rgba(189, 147, 249, 0.6)',

    errorBgSubtle: 'rgba(255, 85, 85, 0.1)',
    errorBg: 'rgba(255, 85, 85, 0.15)',

    highlightBgSubtle: 'rgba(255, 121, 198, 0.1)',

    cmSelectionBg: 'rgba(189, 147, 249, 0.25)',

    shadowSm: '0 1px 2px rgba(0, 0, 0, 0.3)',
    shadowMd: '0 4px 6px rgba(0, 0, 0, 0.4)',
    shadowLg: '0 10px 15px rgba(0, 0, 0, 0.5)',
    shadowSubtle: '0 1px 3px rgba(0, 0, 0, 0.3)',
    shadowCard: '0 2px 8px rgba(0, 0, 0, 0.4)',
  },
}

export const AYU_THEME: Theme = {
  id: 'ayu',
  name: 'Ayu',
  isDark: true,
  colors: {
    bgPrimary: '#020202',
    // bgPrimary: '#0b0e14',
    bgSecondary: '#0d1017',
    bgTertiary: '#131721',
    bgElevated: '#1c1f2b',
    bgHover: '#131721',

    borderSubtle: '#1c1f2b',
    borderDefault: '#3a3d4a',
    borderFocus: '#e6b450',

    textPrimary: '#bfbdb6',
    textSecondary: '#8b8680',
    textMuted: '#565b66',

    accentPrimary: '#e6b450',
    accentPrimaryHover: '#f0c566',
    accentSecondary: '#7fd962',
    accentWarning: '#e6b450',
    accentError: '#d95757',
    accentErrorHover: '#e57070',
    accentHighlight: '#f07178',

    canvasBg: '#080a10',
    canvasGridBgAlt: '#060810',
    canvasGridFine: '#1c1f2b',
    canvasHoverText: '#8b8680',

    overlayStrong: 'rgba(191, 189, 182, 0.8)',
    overlayMediumStrong: 'rgba(191, 189, 182, 0.7)',
    overlayMedium: 'rgba(191, 189, 182, 0.5)',
    overlaySubtle: 'rgba(191, 189, 182, 0.2)',
    overlayFaint: 'rgba(191, 189, 182, 0.1)',
    overlayGhost: 'rgba(191, 189, 182, 0.06)',
    overlayWhisper: 'rgba(191, 189, 182, 0.04)',
    overlayTrace: 'rgba(191, 189, 182, 0.03)',

    accentPrimaryAlpha08: 'rgba(230, 180, 80, 0.08)',
    accentPrimaryAlpha30: 'rgba(230, 180, 80, 0.3)',
    accentPrimaryAlpha60: 'rgba(230, 180, 80, 0.6)',

    errorBgSubtle: 'rgba(217, 87, 87, 0.1)',
    errorBg: 'rgba(217, 87, 87, 0.15)',

    highlightBgSubtle: 'rgba(240, 113, 120, 0.1)',

    cmSelectionBg: 'rgba(230, 180, 80, 0.2)',

    shadowSm: '0 1px 2px rgba(0, 0, 0, 0.4)',
    shadowMd: '0 4px 6px rgba(0, 0, 0, 0.5)',
    shadowLg: '0 10px 15px rgba(0, 0, 0, 0.6)',
    shadowSubtle: '0 1px 3px rgba(0, 0, 0, 0.4)',
    shadowCard: '0 2px 8px rgba(0, 0, 0, 0.5)',
  },
}

export const TOKYO_NIGHT_THEME: Theme = {
  id: 'tokyo-night',
  name: 'Tokyo Night',
  isDark: true,
  colors: {
    bgPrimary: '#0a0b16',
    bgSecondary: '#1f2030',
    bgTertiary: '#292e42',
    bgElevated: '#33384e',
    bgHover: '#292e42',

    borderSubtle: '#292e42',
    borderDefault: '#3b4261',
    borderFocus: '#7aa2f7',

    textPrimary: '#c0caf5',
    textSecondary: '#a9b1d6',
    textMuted: '#666f89',

    accentPrimary: '#7aa2f7',
    accentPrimaryHover: '#89b4fa',
    accentSecondary: '#9ece6a',
    accentWarning: '#e0af68',
    accentError: '#f7768e',
    accentErrorHover: '#ff899e',
    accentHighlight: '#bb9af7',

    canvasBg: '#16161e',
    canvasGridBgAlt: '#111118',
    canvasGridFine: '#292e42',
    canvasHoverText: '#a9b1d6',

    overlayStrong: 'rgba(192, 202, 245, 0.8)',
    overlayMediumStrong: 'rgba(192, 202, 245, 0.7)',
    overlayMedium: 'rgba(192, 202, 245, 0.5)',
    overlaySubtle: 'rgba(192, 202, 245, 0.2)',
    overlayFaint: 'rgba(192, 202, 245, 0.1)',
    overlayGhost: 'rgba(192, 202, 245, 0.06)',
    overlayWhisper: 'rgba(192, 202, 245, 0.04)',
    overlayTrace: 'rgba(192, 202, 245, 0.03)',

    accentPrimaryAlpha08: 'rgba(122, 162, 247, 0.08)',
    accentPrimaryAlpha30: 'rgba(122, 162, 247, 0.3)',
    accentPrimaryAlpha60: 'rgba(122, 162, 247, 0.6)',

    errorBgSubtle: 'rgba(247, 118, 142, 0.1)',
    errorBg: 'rgba(247, 118, 142, 0.15)',

    highlightBgSubtle: 'rgba(187, 154, 247, 0.1)',

    cmSelectionBg: 'rgba(122, 162, 247, 0.25)',

    shadowSm: '0 1px 2px rgba(0, 0, 0, 0.4)',
    shadowMd: '0 4px 6px rgba(0, 0, 0, 0.5)',
    shadowLg: '0 10px 15px rgba(0, 0, 0, 0.6)',
    shadowSubtle: '0 1px 3px rgba(0, 0, 0, 0.4)',
    shadowCard: '0 2px 8px rgba(0, 0, 0, 0.5)',
  },
}

export const ROSE_PINE_THEME: Theme = {
  id: 'rose-pine',
  name: 'Rosé Pine',
  isDark: true,
  colors: {
    bgPrimary: '#090714',
    bgSecondary: '#1f1d2e',
    bgTertiary: '#26233a',
    bgElevated: '#2a283e',
    bgHover: '#26233a',

    borderSubtle: '#26233a',
    borderDefault: '#393552',
    borderFocus: '#c4a7e7',

    textPrimary: '#e0def4',
    textSecondary: '#908caa',
    textMuted: '#6e6a86',

    accentPrimary: '#c4a7e7',
    accentPrimaryHover: '#d0b8ef',
    accentSecondary: '#31748f',
    accentWarning: '#f6c177',
    accentError: '#eb6f92',
    accentErrorHover: '#f08ba8',
    accentHighlight: '#ebbcba',

    canvasBg: '#15131f',
    canvasGridBgAlt: '#110f1a',
    canvasGridFine: '#26233a',
    canvasHoverText: '#908caa',

    overlayStrong: 'rgba(224, 222, 244, 0.8)',
    overlayMediumStrong: 'rgba(224, 222, 244, 0.7)',
    overlayMedium: 'rgba(224, 222, 244, 0.5)',
    overlaySubtle: 'rgba(224, 222, 244, 0.2)',
    overlayFaint: 'rgba(224, 222, 244, 0.1)',
    overlayGhost: 'rgba(224, 222, 244, 0.06)',
    overlayWhisper: 'rgba(224, 222, 244, 0.04)',
    overlayTrace: 'rgba(224, 222, 244, 0.03)',

    accentPrimaryAlpha08: 'rgba(196, 167, 231, 0.08)',
    accentPrimaryAlpha30: 'rgba(196, 167, 231, 0.3)',
    accentPrimaryAlpha60: 'rgba(196, 167, 231, 0.6)',

    errorBgSubtle: 'rgba(235, 111, 146, 0.1)',
    errorBg: 'rgba(235, 111, 146, 0.15)',

    highlightBgSubtle: 'rgba(235, 188, 186, 0.1)',

    cmSelectionBg: 'rgba(196, 167, 231, 0.25)',

    shadowSm: '0 1px 2px rgba(0, 0, 0, 0.4)',
    shadowMd: '0 4px 6px rgba(0, 0, 0, 0.5)',
    shadowLg: '0 10px 15px rgba(0, 0, 0, 0.6)',
    shadowSubtle: '0 1px 3px rgba(0, 0, 0, 0.4)',
    shadowCard: '0 2px 8px rgba(0, 0, 0, 0.5)',
  },
}

export const MATCHA_THEME: Theme = {
  id: 'matcha',
  name: 'Matcha',
  isDark: false,
  colors: {
    bgPrimary: '#eef3e8',
    bgSecondary: '#f6faf0',
    bgTertiary: '#d6e0cc',
    bgElevated: '#e3ebd8',
    bgHover: '#d6e0cc',

    borderSubtle: '#c8d4b8',
    borderDefault: '#adbf9a',
    borderFocus: '#7a9a5a',

    textPrimary: '#2a3520',
    textSecondary: '#556845',
    textMuted: '#8a9a78',

    accentPrimary: '#5a8a3a',
    accentPrimaryHover: '#4a7530',
    accentSecondary: '#3a8a7a',
    accentWarning: '#a08a20',
    accentError: '#c04040',
    accentErrorHover: '#a03030',
    accentHighlight: '#8a5a90',

    canvasBg: '#eef3e8',
    canvasGridBgAlt: '#e3ebd8',
    canvasGridFine: '#c8d4b8',
    canvasHoverText: '#556845',

    overlayStrong: 'rgba(42, 53, 32, 0.7)',
    overlayMediumStrong: 'rgba(42, 53, 32, 0.55)',
    overlayMedium: 'rgba(42, 53, 32, 0.4)',
    overlaySubtle: 'rgba(42, 53, 32, 0.15)',
    overlayFaint: 'rgba(42, 53, 32, 0.08)',
    overlayGhost: 'rgba(42, 53, 32, 0.06)',
    overlayWhisper: 'rgba(42, 53, 32, 0.04)',
    overlayTrace: 'rgba(42, 53, 32, 0.03)',

    accentPrimaryAlpha08: 'rgba(90, 138, 58, 0.1)',
    accentPrimaryAlpha30: 'rgba(90, 138, 58, 0.3)',
    accentPrimaryAlpha60: 'rgba(90, 138, 58, 0.6)',

    errorBgSubtle: 'rgba(192, 64, 64, 0.08)',
    errorBg: 'rgba(192, 64, 64, 0.12)',

    highlightBgSubtle: 'rgba(138, 90, 144, 0.1)',

    cmSelectionBg: 'rgba(90, 138, 58, 0.2)',

    shadowSm: '0 1px 2px rgba(42, 53, 32, 0.08)',
    shadowMd: '0 4px 6px rgba(42, 53, 32, 0.1)',
    shadowLg: '0 10px 15px rgba(42, 53, 32, 0.12)',
    shadowSubtle: '0 1px 3px rgba(42, 53, 32, 0.06)',
    shadowCard: '0 2px 8px rgba(42, 53, 32, 0.08)',
  },
}

export const SAND_THEME: Theme = {
  id: 'sand',
  name: 'Sand',
  isDark: false,
  colors: {
    bgPrimary: '#f2ebe0',
    bgSecondary: '#faf5ec',
    bgTertiary: '#ddd4c5',
    bgElevated: '#e8e0d2',
    bgHover: '#ddd4c5',

    borderSubtle: '#d0c5b4',
    borderDefault: '#bbb0a0',
    borderFocus: '#9a8a70',

    textPrimary: '#3a3020',
    textSecondary: '#6a5a44',
    textMuted: '#9a8a70',

    accentPrimary: '#b07830',
    accentPrimaryHover: '#966020',
    accentSecondary: '#6a8a40',
    accentWarning: '#b09020',
    accentError: '#c04040',
    accentErrorHover: '#a03030',
    accentHighlight: '#a06060',

    canvasBg: '#f2ebe0',
    canvasGridBgAlt: '#e8e0d2',
    canvasGridFine: '#d0c5b4',
    canvasHoverText: '#6a5a44',

    overlayStrong: 'rgba(58, 48, 32, 0.7)',
    overlayMediumStrong: 'rgba(58, 48, 32, 0.55)',
    overlayMedium: 'rgba(58, 48, 32, 0.4)',
    overlaySubtle: 'rgba(58, 48, 32, 0.15)',
    overlayFaint: 'rgba(58, 48, 32, 0.08)',
    overlayGhost: 'rgba(58, 48, 32, 0.06)',
    overlayWhisper: 'rgba(58, 48, 32, 0.04)',
    overlayTrace: 'rgba(58, 48, 32, 0.03)',

    accentPrimaryAlpha08: 'rgba(176, 120, 48, 0.1)',
    accentPrimaryAlpha30: 'rgba(176, 120, 48, 0.3)',
    accentPrimaryAlpha60: 'rgba(176, 120, 48, 0.6)',

    errorBgSubtle: 'rgba(192, 64, 64, 0.08)',
    errorBg: 'rgba(192, 64, 64, 0.12)',

    highlightBgSubtle: 'rgba(160, 96, 96, 0.1)',

    cmSelectionBg: 'rgba(176, 120, 48, 0.2)',

    shadowSm: '0 1px 2px rgba(58, 48, 32, 0.08)',
    shadowMd: '0 4px 6px rgba(58, 48, 32, 0.1)',
    shadowLg: '0 10px 15px rgba(58, 48, 32, 0.12)',
    shadowSubtle: '0 1px 3px rgba(58, 48, 32, 0.06)',
    shadowCard: '0 2px 8px rgba(58, 48, 32, 0.08)',
  },
}

export const BRAUN_THEME: Theme = {
  id: 'braun',
  name: 'Braun',
  isDark: false,
  colors: {
    bgPrimary: '#f7ecc9',
    bgSecondary: '#fff5df',
    bgTertiary: '#e8d8b9',
    bgElevated: '#ead8ba',
    bgHover: '#e8d8b9',

    borderSubtle: '#ccc4b0',
    borderDefault: '#b5ad98',
    borderFocus: '#8a8270',

    textPrimary: '#2c2820',
    textSecondary: '#5a5448',
    textMuted: '#8a8270',

    accentPrimary: '#cc3d20',
    accentPrimaryHover: '#b03018',
    accentSecondary: '#2060b0',
    accentWarning: '#d08020',
    accentError: '#cc3d20',
    accentErrorHover: '#b03018',
    accentHighlight: '#e07020',

    canvasBg: '#f0e8d8',
    canvasGridBgAlt: '#e5ddd0',
    canvasGridFine: '#ccc4b0',
    canvasHoverText: '#5a5448',

    overlayStrong: 'rgba(44, 40, 32, 0.7)',
    overlayMediumStrong: 'rgba(44, 40, 32, 0.55)',
    overlayMedium: 'rgba(44, 40, 32, 0.4)',
    overlaySubtle: 'rgba(44, 40, 32, 0.15)',
    overlayFaint: 'rgba(44, 40, 32, 0.08)',
    overlayGhost: 'rgba(44, 40, 32, 0.06)',
    overlayWhisper: 'rgba(44, 40, 32, 0.04)',
    overlayTrace: 'rgba(44, 40, 32, 0.03)',

    accentPrimaryAlpha08: 'rgba(204, 61, 32, 0.1)',
    accentPrimaryAlpha30: 'rgba(204, 61, 32, 0.3)',
    accentPrimaryAlpha60: 'rgba(204, 61, 32, 0.6)',

    errorBgSubtle: 'rgba(204, 61, 32, 0.08)',
    errorBg: 'rgba(204, 61, 32, 0.12)',

    highlightBgSubtle: 'rgba(224, 112, 32, 0.1)',

    cmSelectionBg: 'rgba(32, 96, 176, 0.2)',

    shadowSm: '0 1px 2px rgba(44, 40, 32, 0.08)',
    shadowMd: '0 4px 6px rgba(44, 40, 32, 0.1)',
    shadowLg: '0 10px 15px rgba(44, 40, 32, 0.12)',
    shadowSubtle: '0 1px 3px rgba(44, 40, 32, 0.06)',
    shadowCard: '0 2px 8px rgba(44, 40, 32, 0.08)',
  },
}

export const LAVENDER_THEME: Theme = {
  id: 'lavender',
  name: 'Lavender',
  isDark: false,
  colors: {
    bgPrimary: '#ece8f4',
    bgSecondary: '#f5f2fa',
    bgTertiary: '#d8d2e4',
    bgElevated: '#e2dced',
    bgHover: '#d8d2e4',

    borderSubtle: '#ccc4d8',
    borderDefault: '#b5aac5',
    borderFocus: '#8a7aaa',

    textPrimary: '#2a2535',
    textSecondary: '#555068',
    textMuted: '#8a7aaa',

    accentPrimary: '#7a5aaa',
    accentPrimaryHover: '#664a95',
    accentSecondary: '#5a8aaa',
    accentWarning: '#a08a30',
    accentError: '#c04050',
    accentErrorHover: '#a03040',
    accentHighlight: '#c06090',

    canvasBg: '#ece8f4',
    canvasGridBgAlt: '#e2dced',
    canvasGridFine: '#ccc4d8',
    canvasHoverText: '#555068',

    overlayStrong: 'rgba(42, 37, 53, 0.7)',
    overlayMediumStrong: 'rgba(42, 37, 53, 0.55)',
    overlayMedium: 'rgba(42, 37, 53, 0.4)',
    overlaySubtle: 'rgba(42, 37, 53, 0.15)',
    overlayFaint: 'rgba(42, 37, 53, 0.08)',
    overlayGhost: 'rgba(42, 37, 53, 0.06)',
    overlayWhisper: 'rgba(42, 37, 53, 0.04)',
    overlayTrace: 'rgba(42, 37, 53, 0.03)',

    accentPrimaryAlpha08: 'rgba(122, 90, 170, 0.1)',
    accentPrimaryAlpha30: 'rgba(122, 90, 170, 0.3)',
    accentPrimaryAlpha60: 'rgba(122, 90, 170, 0.6)',

    errorBgSubtle: 'rgba(192, 64, 80, 0.08)',
    errorBg: 'rgba(192, 64, 80, 0.12)',

    highlightBgSubtle: 'rgba(192, 96, 144, 0.1)',

    cmSelectionBg: 'rgba(122, 90, 170, 0.2)',

    shadowSm: '0 1px 2px rgba(42, 37, 53, 0.08)',
    shadowMd: '0 4px 6px rgba(42, 37, 53, 0.1)',
    shadowLg: '0 10px 15px rgba(42, 37, 53, 0.12)',
    shadowSubtle: '0 1px 3px rgba(42, 37, 53, 0.06)',
    shadowCard: '0 2px 8px rgba(42, 37, 53, 0.08)',
  },
}

export const MONO_DARK_THEME: Theme = {
  id: 'mono-dark',
  name: 'Mono Dark',
  isDark: true,
  colors: {
    bgPrimary: '#0a0a0a',
    bgSecondary: '#141414',
    bgTertiary: '#1e1e1e',
    bgElevated: '#262626',
    bgHover: '#1e1e1e',

    borderSubtle: '#282828',
    borderDefault: '#4a4a4a',
    borderFocus: '#666666',

    textPrimary: '#d4d4d4',
    textSecondary: '#909090',
    textMuted: '#606060',

    accentPrimary: '#b0b0b0',
    accentPrimaryHover: '#cccccc',
    accentSecondary: '#888888',
    accentWarning: '#b0b0b0',
    accentError: '#a0a0a0',
    accentErrorHover: '#c0c0c0',
    accentHighlight: '#909090',

    canvasBg: '#0a0a0a',
    canvasGridBgAlt: '#060606',
    canvasGridFine: '#1a1a1a',
    canvasHoverText: '#909090',

    overlayStrong: 'rgba(212, 212, 212, 0.8)',
    overlayMediumStrong: 'rgba(212, 212, 212, 0.7)',
    overlayMedium: 'rgba(212, 212, 212, 0.5)',
    overlaySubtle: 'rgba(212, 212, 212, 0.2)',
    overlayFaint: 'rgba(212, 212, 212, 0.1)',
    overlayGhost: 'rgba(212, 212, 212, 0.06)',
    overlayWhisper: 'rgba(212, 212, 212, 0.04)',
    overlayTrace: 'rgba(212, 212, 212, 0.03)',

    accentPrimaryAlpha08: 'rgba(176, 176, 176, 0.08)',
    accentPrimaryAlpha30: 'rgba(176, 176, 176, 0.3)',
    accentPrimaryAlpha60: 'rgba(176, 176, 176, 0.6)',

    errorBgSubtle: 'rgba(160, 160, 160, 0.1)',
    errorBg: 'rgba(160, 160, 160, 0.15)',

    highlightBgSubtle: 'rgba(144, 144, 144, 0.1)',

    cmSelectionBg: 'rgba(176, 176, 176, 0.25)',

    shadowSm: '0 1px 2px rgba(0, 0, 0, 0.4)',
    shadowMd: '0 4px 6px rgba(0, 0, 0, 0.5)',
    shadowLg: '0 10px 15px rgba(0, 0, 0, 0.6)',
    shadowSubtle: '0 1px 3px rgba(0, 0, 0, 0.4)',
    shadowCard: '0 2px 8px rgba(0, 0, 0, 0.5)',
  },
}

export const MONO_LIGHT_THEME: Theme = {
  id: 'mono-light',
  name: 'Mono Light',
  isDark: false,
  colors: {
    bgPrimary: '#f0f0f0',
    bgSecondary: '#fafafa',
    bgTertiary: '#d8d8d8',
    bgElevated: '#e6e6e6',
    bgHover: '#d8d8d8',

    borderSubtle: '#cccccc',
    borderDefault: '#b0b0b0',
    borderFocus: '#666666',

    textPrimary: '#1a1a1a',
    textSecondary: '#555555',
    textMuted: '#888888',

    accentPrimary: '#444444',
    accentPrimaryHover: '#2a2a2a',
    accentSecondary: '#666666',
    accentWarning: '#555555',
    accentError: '#505050',
    accentErrorHover: '#333333',
    accentHighlight: '#606060',

    canvasBg: '#f0f0f0',
    canvasGridBgAlt: '#e6e6e6',
    canvasGridFine: '#cccccc',
    canvasHoverText: '#555555',

    overlayStrong: 'rgba(26, 26, 26, 0.7)',
    overlayMediumStrong: 'rgba(26, 26, 26, 0.55)',
    overlayMedium: 'rgba(26, 26, 26, 0.4)',
    overlaySubtle: 'rgba(26, 26, 26, 0.15)',
    overlayFaint: 'rgba(26, 26, 26, 0.08)',
    overlayGhost: 'rgba(26, 26, 26, 0.06)',
    overlayWhisper: 'rgba(26, 26, 26, 0.04)',
    overlayTrace: 'rgba(26, 26, 26, 0.03)',

    accentPrimaryAlpha08: 'rgba(68, 68, 68, 0.1)',
    accentPrimaryAlpha30: 'rgba(68, 68, 68, 0.3)',
    accentPrimaryAlpha60: 'rgba(68, 68, 68, 0.6)',

    errorBgSubtle: 'rgba(80, 80, 80, 0.08)',
    errorBg: 'rgba(80, 80, 80, 0.12)',

    highlightBgSubtle: 'rgba(96, 96, 96, 0.1)',

    cmSelectionBg: 'rgba(68, 68, 68, 0.2)',

    shadowSm: '0 1px 2px rgba(0, 0, 0, 0.08)',
    shadowMd: '0 4px 6px rgba(0, 0, 0, 0.1)',
    shadowLg: '0 10px 15px rgba(0, 0, 0, 0.12)',
    shadowSubtle: '0 1px 3px rgba(0, 0, 0, 0.06)',
    shadowCard: '0 2px 8px rgba(0, 0, 0, 0.08)',
  },
}

// ---------------------------------------------------------------------------
// Theme registry
// ---------------------------------------------------------------------------

const BUILT_IN_THEMES: Theme[] = [
  DARK_THEME,
  LIGHT_THEME,
  MONOKAI_THEME,
  SOLARIZED_DARK_THEME,
  NORD_THEME,
  DRACULA_THEME,
  AYU_THEME,
  TOKYO_NIGHT_THEME,
  ROSE_PINE_THEME,
  MONO_DARK_THEME,
  WARM_THEME,
  MATCHA_THEME,
  SAND_THEME,
  BRAUN_THEME,
  LAVENDER_THEME,
  MONO_LIGHT_THEME,
]
const themeRegistry = new Map<string, Theme>()

// Initialize with built-ins
BUILT_IN_THEMES.forEach((t) => themeRegistry.set(t.id, t))

export function getThemeById(id: string): Theme {
  return themeRegistry.get(id) ?? DARK_THEME
}

export function getAllThemes(): Theme[] {
  return Array.from(themeRegistry.values())
}

export function registerTheme(theme: Theme): void {
  themeRegistry.set(theme.id, theme)
}

export function createTheme(
  id: string,
  name: string,
  base: Theme,
  overrides: Partial<ThemeColors>,
): Theme {
  return {
    id,
    name,
    isDark: base.isDark,
    colors: { ...base.colors, ...overrides },
  }
}

// ---------------------------------------------------------------------------
// Theme application
// ---------------------------------------------------------------------------

/** Maps ThemeColors keys to CSS custom property names. */
const COLOR_TO_CSS_VAR: Record<keyof ThemeColors, string> = {
  bgPrimary: '--color-bg-primary',
  bgSecondary: '--color-bg-secondary',
  bgTertiary: '--color-bg-tertiary',
  bgElevated: '--color-bg-elevated',
  bgHover: '--color-bg-hover',

  borderSubtle: '--color-border-subtle',
  borderDefault: '--color-border-default',
  borderFocus: '--color-border-focus',

  textPrimary: '--color-text-primary',
  textSecondary: '--color-text-secondary',
  textMuted: '--color-text-muted',

  accentPrimary: '--color-accent-primary',
  accentPrimaryHover: '--color-accent-primary-hover',
  accentSecondary: '--color-accent-secondary',
  accentWarning: '--color-accent-warning',
  accentError: '--color-accent-error',
  accentErrorHover: '--color-accent-error-hover',
  accentHighlight: '--color-accent-highlight',

  canvasBg: '--color-canvas-bg',
  canvasGridBgAlt: '--color-canvas-grid-bg-alt',
  canvasGridFine: '--color-canvas-grid-fine',
  canvasHoverText: '--color-canvas-hover-text',

  overlayStrong: '--color-overlay-strong',
  overlayMediumStrong: '--color-overlay-medium-strong',
  overlayMedium: '--color-overlay-medium',
  overlaySubtle: '--color-overlay-subtle',
  overlayFaint: '--color-overlay-faint',
  overlayGhost: '--color-overlay-ghost',
  overlayWhisper: '--color-overlay-whisper',
  overlayTrace: '--color-overlay-trace',

  accentPrimaryAlpha08: '--color-accent-primary-08',
  accentPrimaryAlpha30: '--color-accent-primary-30',
  accentPrimaryAlpha60: '--color-accent-primary-60',

  errorBgSubtle: '--color-error-bg-subtle',
  errorBg: '--color-error-bg',

  highlightBgSubtle: '--color-highlight-bg-subtle',

  cmSelectionBg: '--color-cm-selection-bg',

  shadowSm: '--shadow-sm',
  shadowMd: '--shadow-md',
  shadowLg: '--shadow-lg',
  shadowSubtle: '--shadow-subtle',
  shadowCard: '--shadow-card',
}

/**
 * Apply a theme by setting all CSS custom properties on the document root.
 * Also sets color-scheme for native form elements / scrollbars.
 */
export function applyTheme(theme: Theme): void {
  const root = document.documentElement

  for (const [key, cssVar] of Object.entries(COLOR_TO_CSS_VAR)) {
    root.style.setProperty(cssVar, theme.colors[key as keyof ThemeColors])
  }

  root.style.setProperty('color-scheme', theme.isDark ? 'dark' : 'light')
}
